import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { Colors } from "../Colors";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { IconButton, MenuItem, Select, TextField, Typography } from "@mui/material";
import { AppointmentType, CompleteTemplatePlanOfCare, CompleteTemplatePlanOfCareWeek, TemplatePlanOfCareAppointment } from "../types";
import { filterAppointmentTypesForPicker } from "../utils/appointmentTypes";
import { v4 as uuidv4 } from "uuid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

interface TextInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  mb?: number; // TODO (Hizami): Probably not the best way to do this
  ml?: number;
  readOnly?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({ label, value, onChange, mb, ml, readOnly }) => {
  return (
    <TextField
      value={value}
      onChange={onChange}
      variant="outlined"
      InputProps={{
        readOnly: readOnly || false,
      }}
      sx={{
        marginLeft: "8px",
        "& .MuiInputBase-input": {
          color: "#FFF",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          background: Colors.textfield,
        },
        "& .MuiOutlinedInput-input": {
          padding: "6px 15px",
        },
      }}
    />
  );
};

interface NumberInputProps {
  disabled: boolean;
  value: number;
  setter: (value: number) => void;
}

const NumberInput: React.FC<NumberInputProps> = ({ disabled, value, setter }) => {
  return (
    <TextField
      type="number"
      value={value}
      onChange={(e) => setter(parseInt(e.target.value))}
      variant="outlined"
      size="small"
      disabled={disabled}
      sx={{
        width: "45px",
        margin: "0 8px",
        "& .MuiInputBase-input": {
          color: "#FFF",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          background: Colors.textfield,
        },
        "& .MuiOutlinedInput-input": {
          padding: "2px",
          textAlign: "center",
        },
        "& input[type=number]": {
          "-moz-appearance": "textfield", // Firefox
          "&::-webkit-outer-spin-button": {
            "-webkit-appearance": "none", // Chrome, Safari, Edge, Opera
            margin: 0,
          },
          "&::-webkit-inner-spin-button": {
            "-webkit-appearance": "none", // Chrome, Safari, Edge, Opera
            margin: 0,
          },
        },
      }}
    />
  );
};

interface DropdownInputProps {
  options: { value: string; label: string }[];
  value: string;
  setter: (value: string) => void;
}

const Dropdown: React.FC<DropdownInputProps> = ({ options, value, setter }) => {
  return (
    <Select
      value={value}
      onChange={(e) => setter(e.target.value)}
      displayEmpty
      IconComponent={ExpandMoreIcon}
      sx={{
        width: "auto", // shrink to fit content
        color: Colors.primary,
        backgroundColor: "transparent",
        boxShadow: "none",
        ".MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          padding: 0,
          color: Colors.primary,
          fontWeight: "bold",
        },
        "& fieldset": {
          border: "none",
        },
        "&:hover fieldset": {
          border: "none",
        },
        "&.Mui-focused fieldset": {
          border: "none",
        },
        ".MuiSelect-icon": {
          color: Colors.primary,
          marginLeft: "4px",
          marginRight: "0",
        },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

type AppointmentCardProps = {
  appointment: TemplatePlanOfCareAppointment;
  appointmentNumber: number;
  appointmentTypes: AppointmentType[];
  updatePlanOfCareAppointment: (appointment: TemplatePlanOfCareAppointment) => void;
  handleDeleteAppointment: (appointmentId: string) => void;
  editable: boolean;
  isExtra?: boolean;
};

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  appointmentNumber,
  appointmentTypes,
  updatePlanOfCareAppointment,
  handleDeleteAppointment,
  editable,
  isExtra,
}) => {
  const organization = useSelector((state: RootState) => state.auth.organization);
  const handleAppointmentTypeChange = (value: string) => {
    const updatedAppointment = { ...appointment, appointmentTypeId: value };
    updatePlanOfCareAppointment(updatedAppointment);
  };
  const appointmentType = appointmentTypes.find((type) => type.appointmentTypeId === appointment.appointmentTypeId);
  const appointmentTypeName = appointmentType ? appointmentType.name : "Unknown Appointment Type";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        padding: "8px",
        //border: "1px solid #FFF",
        backgroundColor: Colors.bg,

        borderRadius: "8px",
        //borderColor: appointment.status === "unscheduled" ? Colors.error : Colors.success,

        width: "400px",
      }}
    >
      <Box sx={{ flexGrow: 1 }} />
      <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          {isExtra ? `Not In Plan Of Care` : `Visit #${appointmentNumber}`}
        </Typography>
        {editable ? (
          <Dropdown
            options={filterAppointmentTypesForPicker(
              appointmentTypes,
              appointment.appointmentTypeId,
              organization?.organizationId
            ).map((type) => ({
              value: type.appointmentTypeId,
              label: type.name,
            }))}
            value={appointment.appointmentTypeId}
            setter={handleAppointmentTypeChange}
          />
        ) : (
          <Typography variant="body1">{appointmentTypeName}</Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {editable && (
        <IconButton
          onClick={() => handleDeleteAppointment(appointment.templatePlanOfCareAppointmentId)}
          sx={{
            backgroundColor: "transparent",
            border: "1px solid white",
            color: "white",
            width: "24px",
            height: "24px",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <RemoveIcon />
        </IconButton>
      )}
    </Box>
  );
};

type WeekRowProps = {
  week: CompleteTemplatePlanOfCareWeek;
  weekNumber: number;
  baseAppointmentNumber: number;
  appointmentTypes: AppointmentType[];
  updatePlanOfCareWeek: (week: CompleteTemplatePlanOfCareWeek) => void;
  handleDeleteWeek: (weekId: string) => void;
  defaultAppointmentTypeId: string;
  editable: boolean;
};

const WeekRow: React.FC<WeekRowProps> = ({
  week,
  weekNumber,
  baseAppointmentNumber,
  appointmentTypes,
  updatePlanOfCareWeek,
  handleDeleteWeek,
  defaultAppointmentTypeId,
  editable,
}) => {
  const updatePlanOfCareAppointment = (appointment: TemplatePlanOfCareAppointment) => {
    const updatedAppointments = week.templatePlanOfCareAppointments.map((a) =>
      a.templatePlanOfCareAppointmentId === appointment.templatePlanOfCareAppointmentId ? appointment : a
    );
    const updatedAppointmentIds = updatedAppointments.map((a) => a.templatePlanOfCareAppointmentId);
    const updatedWeek = { ...week, templatePlanOfCareAppointments: updatedAppointments, templatePlanOfCareAppointmentIds: updatedAppointmentIds };
    updatePlanOfCareWeek(updatedWeek);
  };

  const handleDeleteAppointment = (appointmentId: string) => {
    const updatedAppointments = week.templatePlanOfCareAppointments.filter((a) => a.templatePlanOfCareAppointmentId !== appointmentId);
    const updatedAppointmentIds = updatedAppointments.map((a) => a.templatePlanOfCareAppointmentId);
    const updatedWeek = { ...week, templatePlanOfCareAppointments: updatedAppointments, templatePlanOfCareAppointmentIds: updatedAppointmentIds };
    updatePlanOfCareWeek(updatedWeek);
  };

  const handleAddAppointment = () => {
    const newAppointmentId = uuidv4();
    const newAppointment: TemplatePlanOfCareAppointment = {
      superOrganizationId: week.superOrganizationId,
      organizationId: week.organizationId,

      templatePlanOfCareId: week.templatePlanOfCareId,
      templatePlanOfCareWeekId: week.templatePlanOfCareWeekId,
      templatePlanOfCareAppointmentId: newAppointmentId,

      appointmentTypeId: defaultAppointmentTypeId,
    };
    const updatedAppointments = [...week.templatePlanOfCareAppointments, newAppointment];
    const updatedAppointmentIds = [...week.templatePlanOfCareAppointmentIds, newAppointmentId];
    const updatedWeek = { ...week, templatePlanOfCareAppointments: updatedAppointments, templatePlanOfCareAppointmentIds: updatedAppointmentIds };
    updatePlanOfCareWeek(updatedWeek);
  };

  return (
    <Box
      flexDirection={"row"}
      display="flex"
      justifyContent="flex-start"
      gap="20px"
      sx={{ borderRadius: "8px", backgroundColor: Colors.secondary, padding: "16px", margin: "8px 0" }}
    >
      <Box flexDirection={"column"} display="flex" justifyContent="flexStart" width="120px">
        <Typography variant="body1">Week {weekNumber}</Typography>
      </Box>

      {week.templatePlanOfCareAppointments.map((appointment, j) => {
        const appointmentNumber = baseAppointmentNumber + j + 1;
        return (
          <AppointmentCard
            key={appointment.templatePlanOfCareAppointmentId}
            appointment={appointment}
            appointmentNumber={appointmentNumber}
            appointmentTypes={appointmentTypes}
            updatePlanOfCareAppointment={updatePlanOfCareAppointment}
            handleDeleteAppointment={handleDeleteAppointment}
            editable={editable}
          />
        );
      })}

      <Box sx={{ flexGrow: 1 }} />

      {editable && (
        <Button
          onClick={handleAddAppointment}
          sx={{
            color: "white",
            marginTop: "8px",
            padding: "12px 16px",
            borderRadius: "12px", // curved edges
            background: Colors.secondary,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textTransform: "none", // keep text normal case
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 24, marginBottom: "4px" }} />
          Add visit
        </Button>
      )}
      {/*editable && (
        <Button variant="outlined" color="error" onClick={() => handleDeleteWeek(week.activePlanOfCarePeriodId)} sx={{ marginTop: "8px" }}>
          Delete Week
        </Button>
      )*/}
    </Box>
  );
};

export const PlanOfCareTemplateCreatePage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);

  React.useEffect(() => {
    const fetchAppointmentTypes = async () => {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const sortedAppointmentTypes = response.data.sort((a: any, b: any) => a.name.localeCompare(b.name)) as AppointmentType[];
      setAppointmentTypes(sortedAppointmentTypes);

      const followUp = sortedAppointmentTypes.find((type) => type.name === "Follow-Up"); // TODO (Hizami): webpt hardcoded
      const initialExamination = sortedAppointmentTypes.find((type) => type.name === "Initial Examination"); // TODO (Hizami): webpt hardcoded

      if (followUp) {
        setMostVisits(followUp);
      }

      if (initialExamination) {
        setFirstVisit(initialExamination);
      }
    };

    if (user?.token) {
      fetchAppointmentTypes();
    }
  }, [user?.token]);

  const [title, setTitle] = React.useState("Plan of care template");
  const [numberOfVisits, setNumberOfVisits] = React.useState(0);
  const [visitsPerWeek, setVisitsPerWeek] = React.useState(0);
  const [firstVisit, setFirstVisit] = React.useState<AppointmentType | null>(null);
  const [mostVisits, setMostVisits] = React.useState<AppointmentType | null>(null);
  const [gap, setGap] = React.useState(0);
  const [planOfCare, setPlanOfCare] = React.useState<CompleteTemplatePlanOfCare | null>(null);

  const createPlanOfCare = async () => {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/plan-of-care/templates`, planOfCare, {
      headers: {
        Authorization: `Bearer ${user?.token}`,
      },
    });
    console.log(response.data);
  };

  const updatePlanOfCareWeek = (week: CompleteTemplatePlanOfCareWeek) => {
    setPlanOfCare((prev) => {
      if (!prev) return null;
      const updatedWeeks = prev.templatePlanOfCareWeeks.map((w) => (w.templatePlanOfCareWeekId === week.templatePlanOfCareWeekId ? week : w));
      return { ...prev, templatePlanOfCareWeeks: updatedWeeks };
    });
  };

  const handleDeleteWeek = (weekId: string) => {
    setPlanOfCare((prev) => {
      if (!prev) return null;
      const updatedWeeks = prev.templatePlanOfCareWeeks.filter((w) => w.templatePlanOfCareWeekId !== weekId);
      const updatedWeekIds = updatedWeeks.map((w) => w.templatePlanOfCareWeekId);
      return { ...prev, templatePlanOfCareWeeks: updatedWeeks, templatePlanOfCareWeekIds: updatedWeekIds };
    });
  };

  const handleAddWeek = () => {
    const newWeekId = uuidv4();
    const newWeek: CompleteTemplatePlanOfCareWeek = {
      superOrganizationId: user?.superOrganizationId || "",
      organizationId: user?.organizationId || "",

      templatePlanOfCareId: planOfCare?.templatePlanOfCareId || "",
      templatePlanOfCareWeekId: newWeekId,

      templatePlanOfCareAppointments: [],
      templatePlanOfCareAppointmentIds: [],
    };
    setPlanOfCare((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        templatePlanOfCareWeeks: [...prev.templatePlanOfCareWeeks, newWeek],
        templatePlanOfCareWeekIds: [...prev.templatePlanOfCareWeekIds, newWeekId],
      };
    });
  };

  const generatePlanOfCare = async () => {
    const superOrganizationId = user?.superOrganizationId || "";
    const organizationId = user?.organizationId || "";

    const planOfCareId = uuidv4();
    const template: CompleteTemplatePlanOfCare = {
      superOrganizationId: user?.superOrganizationId || "",
      organizationId: user?.organizationId || "",

      templatePlanOfCareId: planOfCareId,
      name: title,
      numAppointments: numberOfVisits,
      gap: gap,

      templatePlanOfCareWeeks: [],
      templatePlanOfCareWeekIds: [],
    };

    let visitsLeft = numberOfVisits;
    for (let i = 0; i < Math.ceil(numberOfVisits / visitsPerWeek); i++) {
      const weekId = uuidv4();
      const week: CompleteTemplatePlanOfCareWeek = {
        superOrganizationId: superOrganizationId,
        organizationId: organizationId,

        templatePlanOfCareId: planOfCareId,
        templatePlanOfCareWeekId: weekId,

        templatePlanOfCareAppointments: [],
        templatePlanOfCareAppointmentIds: [],
      };
      for (let j = 0; j < visitsPerWeek; j++) {
        if (visitsLeft > 0) {
          let appointmentTypeId = mostVisits?.appointmentTypeId || "";
          if (visitsLeft === numberOfVisits) {
            appointmentTypeId = firstVisit?.appointmentTypeId || "";
          }

          const appointmentId = uuidv4();
          const appointment: TemplatePlanOfCareAppointment = {
            superOrganizationId: superOrganizationId,
            organizationId: organizationId,

            templatePlanOfCareId: planOfCareId,
            templatePlanOfCareWeekId: weekId,
            templatePlanOfCareAppointmentId: appointmentId,

            appointmentTypeId: appointmentTypeId,
          };
          week.templatePlanOfCareAppointments.push(appointment);
          week.templatePlanOfCareAppointmentIds.push(appointmentId);

          visitsLeft--;
        }
      }
      template.templatePlanOfCareWeeks.push(week);
      template.templatePlanOfCareWeekIds.push(weekId);
    }

    setPlanOfCare(template);
  };

  return (
    <Box>
      <SignoutHeader />
      <Typography variant="h1">Plan of Care</Typography>
      <Typography>Name</Typography>
      <TextInput label="Name" value={title} onChange={(e) => setTitle(e.target.value)} />
      <Typography>Treatment schedule</Typography>
      <Typography>How many total visits?</Typography>
      <NumberInput disabled={false} value={numberOfVisits} setter={setNumberOfVisits} />
      <Typography>How many appointments per week?</Typography>
      <NumberInput disabled={false} value={visitsPerWeek} setter={setVisitsPerWeek} />
      <Typography>Gap between visits</Typography>
      <NumberInput disabled={false} value={gap} setter={setGap} />

      <Typography>First visit is:</Typography>
      <Dropdown
        options={filterAppointmentTypesForPicker(
          appointmentTypes,
          firstVisit?.appointmentTypeId,
          user?.organizationId
        ).map((type) => ({
          value: type.appointmentTypeId,
          label: type.name,
        }))}
        value={firstVisit?.appointmentTypeId || ""}
        setter={(value) => {
          const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === value);
          setFirstVisit(selectedType || null);
        }}
      />

      <Typography>Most visits are:</Typography>
      <Dropdown
        options={filterAppointmentTypesForPicker(
          appointmentTypes,
          mostVisits?.appointmentTypeId,
          user?.organizationId
        ).map((type) => ({
          value: type.appointmentTypeId,
          label: type.name,
        }))}
        value={mostVisits?.appointmentTypeId || ""}
        setter={(value) => {
          const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === value);
          setMostVisits(selectedType || null);
        }}
      />

      <Button onClick={generatePlanOfCare}>Generate</Button>
      {planOfCare &&
        planOfCare.templatePlanOfCareWeeks.map((week, i) => {
          const baseAppointmentNumber = planOfCare.templatePlanOfCareWeeks
            .slice(0, i)
            .reduce((sum, w) => sum + w.templatePlanOfCareAppointments.length, 0);
          return (
            <div key={week.templatePlanOfCareWeekId}>
              <WeekRow
                week={week}
                weekNumber={i + 1}
                baseAppointmentNumber={baseAppointmentNumber}
                appointmentTypes={appointmentTypes}
                updatePlanOfCareWeek={updatePlanOfCareWeek}
                handleDeleteWeek={handleDeleteWeek}
                defaultAppointmentTypeId={mostVisits?.appointmentTypeId || ""}
                editable={true}
              />
            </div>
          );
        })}
      {planOfCare && (
        <Box>
          <Button onClick={handleAddWeek} variant="outlined" sx={{ marginTop: "8px" }}>
            Add Week
          </Button>

          {planOfCare.templatePlanOfCareWeeks.length > 1 && (
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                handleDeleteWeek(planOfCare.templatePlanOfCareWeeks[planOfCare.templatePlanOfCareWeeks.length - 1].templatePlanOfCareWeekId);
              }}
            >
              Delete Last Week
            </Button>
          )}
        </Box>
      )}
      {planOfCare && (
        <Button onClick={createPlanOfCare} variant="contained" color="primary">
          Save Plan of Care
        </Button>
      )}
    </Box>
  );
};

export const PlanOfCareTemplateViewPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [planOfCares, setPlanOfCares] = React.useState<CompleteTemplatePlanOfCare[]>([]);
  const [planOfCare, setPlanOfCare] = React.useState<CompleteTemplatePlanOfCare | null>(null);
  const [planOfCareId, setPlanOfCareId] = React.useState<string | null>(null);

  const handlePlanOfCareSelect = (id: string) => {
    const selectedPlanOfCare = planOfCares.find((poc) => poc.templatePlanOfCareId === id);
    setPlanOfCare(selectedPlanOfCare || null);
  };

  const savePlanOfCare = async () => {
    const response = await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/api/plan-of-care/templates/${planOfCare?.templatePlanOfCareId}`,
      planOfCare,
      {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      }
    );
    console.log(response.data);
  };

  const updatePlanOfCareWeek = (week: CompleteTemplatePlanOfCareWeek) => {
    setPlanOfCare((prev) => {
      if (!prev) return null;
      const updatedWeeks = prev.templatePlanOfCareWeeks.map((w) => (w.templatePlanOfCareWeekId === week.templatePlanOfCareWeekId ? week : w));
      return { ...prev, templatePlanOfCareWeeks: updatedWeeks };
    });
  };

  const handleDeleteWeek = (weekId: string) => {
    setPlanOfCare((prev) => {
      if (!prev) return null;
      const updatedWeeks = prev.templatePlanOfCareWeeks.filter((w) => w.templatePlanOfCareWeekId !== weekId);
      const updatedWeekIds = updatedWeeks.map((w) => w.templatePlanOfCareWeekId);
      return { ...prev, templatePlanOfCareWeeks: updatedWeeks, templatePlanOfCareWeekIds: updatedWeekIds };
    });
  };

  const handleAddWeek = () => {
    const newWeekId = uuidv4();
    const newWeek: CompleteTemplatePlanOfCareWeek = {
      superOrganizationId: user?.superOrganizationId || "",
      organizationId: user?.organizationId || "",

      templatePlanOfCareId: planOfCare?.templatePlanOfCareId || "",
      templatePlanOfCareWeekId: newWeekId,

      templatePlanOfCareAppointments: [],
      templatePlanOfCareAppointmentIds: [],
    };
    setPlanOfCare((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        templatePlanOfCareWeeks: [...prev.templatePlanOfCareWeeks, newWeek],
        templatePlanOfCareWeekIds: [...prev.templatePlanOfCareWeekIds, newWeekId],
      };
    });
  };

  const [firstVisit, setFirstVisit] = React.useState<AppointmentType | null>(null);
  const [mostVisits, setMostVisits] = React.useState<AppointmentType | null>(null);

  React.useEffect(() => {
    const fetchPlanOfCares = async () => {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/plan-of-care/templates`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const sortedPlanOfCares = response.data.sort((a: any, b: any) => a.name.localeCompare(b.name)) as CompleteTemplatePlanOfCare[];
      setPlanOfCares(sortedPlanOfCares);
      if (sortedPlanOfCares.length > 0) {
        setPlanOfCare(sortedPlanOfCares[0]);
        setPlanOfCareId(sortedPlanOfCares[0].templatePlanOfCareId);
      }
    };
    const fetchAppointmentTypes = async () => {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const sortedAppointmentTypes = response.data.sort((a: any, b: any) => a.name.localeCompare(b.name)) as AppointmentType[];
      setAppointmentTypes(sortedAppointmentTypes);

      const followUp = sortedAppointmentTypes.find((type) => type.name === "Follow-Up"); // TODO (Hizami): webpt hardcoded
      const initialExamination = sortedAppointmentTypes.find((type) => type.name === "Initial Examination"); // TODO (Hizami): webpt hardcoded

      if (followUp) {
        setMostVisits(followUp);
      }

      if (initialExamination) {
        setFirstVisit(initialExamination);
      }
    };
    const fetchData = async () => {
      await fetchAppointmentTypes();
      await fetchPlanOfCares();
    };
    if (user?.token) {
      fetchData();
    }
  }, [user?.token]);

  return (
    <Box>
      <SignoutHeader />
      <Typography variant="h1">Plan of Care</Typography>

      <Dropdown
        options={planOfCares.map((poc) => ({
          value: poc.templatePlanOfCareId,
          label: poc.name,
        }))}
        value={planOfCareId || ""}
        setter={(value) => {
          setPlanOfCareId(value);
          handlePlanOfCareSelect(value);
        }}
      />

      {planOfCare && (
        <Box>
          {planOfCare.templatePlanOfCareWeeks.map((week, i) => {
            const baseAppointmentNumber = planOfCare.templatePlanOfCareWeeks
              .slice(0, i)
              .reduce((sum, w) => sum + w.templatePlanOfCareAppointments.length, 0);
            return (
              <div key={week.templatePlanOfCareWeekId}>
                <WeekRow
                  week={week}
                  weekNumber={i + 1}
                  baseAppointmentNumber={baseAppointmentNumber}
                  appointmentTypes={appointmentTypes}
                  updatePlanOfCareWeek={updatePlanOfCareWeek}
                  handleDeleteWeek={handleDeleteWeek}
                  defaultAppointmentTypeId={mostVisits?.appointmentTypeId || ""}
                  editable={true}
                />
              </div>
            );
          })}
          <Button onClick={handleAddWeek} variant="outlined" sx={{ marginTop: "8px" }}>
            Add Week
          </Button>
          {planOfCare.templatePlanOfCareWeeks.length > 1 && (
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                handleDeleteWeek(planOfCare.templatePlanOfCareWeeks[planOfCare.templatePlanOfCareWeeks.length - 1].templatePlanOfCareWeekId);
              }}
            >
              Delete Last Week
            </Button>
          )}
          <Typography />
          <Button onClick={savePlanOfCare} variant="contained" color="primary">
            Save Plan of Care
          </Button>
        </Box>
      )}
    </Box>
  );
};
